import { FC, useEffect, useRef } from 'react';
import {
	BoldItalicUnderlineToggles,
	CreateLink,
	MDXEditor,
	MDXEditorProps,
	toolbarPlugin,
	UndoRedo,
	StrikeThroughSupSubToggles,
	ListsToggle,
	StrikeThroughSupSubTogglesProps,
	MDXEditorMethods,
	headingsPlugin,
	listsPlugin,
	linkPlugin,
	markdownShortcutPlugin,
	defaultSvgIcons,
	linkDialogPlugin,
	imagePlugin,
	InsertImage,
} from '@mdxeditor/editor';
import { editorContainerStyles, mdxEditorTheme } from './input.styles';
import { Box, FormHelperText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Global, Interpolation, Theme as EmotionTheme } from '@emotion/react';
import { ChainIcon, StrikeThrough } from '../../icons';
import { Colors } from '../../constants';

export interface TextMarkdownEditorProps extends MDXEditorProps {
	enableUndoRedo?: boolean;
	enableListToggles?: boolean;
	enableLinkPlugin?: boolean;
	enableImagePlugin?: boolean;
	startAdornment?: React.ReactNode;
	strikethroughOptions?: StrikeThroughSupSubTogglesProps['options'];
	disabled?: boolean;
	error?: boolean;
	helperText?: string;
	editorStyles?: Interpolation<EmotionTheme>;
	containerStyles?: Interpolation<EmotionTheme>;
	maxRows?: number;
	minRows?: number;
	onImageUpload?(value: File): Promise<string>;
}

export const TextMarkdownEditor: FC<TextMarkdownEditorProps> = props => {
	const theme = useTheme();
	const {
		enableUndoRedo = true,
		enableListToggles = false,
		enableLinkPlugin = true,
		enableImagePlugin = false,
		strikethroughOptions = ['Strikethrough'],
		markdown,
		onImageUpload,
		onChange,
		disabled,
		error,
		startAdornment,
		helperText,
		translation,
		readOnly = false,
		maxRows,
		minRows,
		editorStyles = mdxEditorTheme(theme, startAdornment, maxRows, minRows),
		containerStyles = {'body .mdxeditor-popup-container': editorContainerStyles(theme)},
		...rest
	} = props;

	const ref = useRef<MDXEditorMethods | null>(null);

	useEffect(() => {
		if (ref.current) {
			ref.current.setMarkdown(markdown);
		}
	}, [markdown]);

	return (
		<Box
			sx={{
				...(startAdornment ? {
					position: 'relative',
				} : {}),
			}}
		>
			<Global
				styles={containerStyles}
			/>

			<Box sx={editorStyles}>
				<MDXEditor
					{...rest}
					ref={ref}
					readOnly={readOnly}
					markdown={markdown}
					onChange={onChange}
					translation={translation}
					iconComponentFor={iconName => {
						switch (iconName) {
							case 'strikeThrough':
								return <StrikeThrough style={{ width: '24px', height: '24px' }} />;
							case 'link':
								return <ChainIcon style={{ width: '24px', height: '20px' }} />;
							default:
								return defaultSvgIcons[iconName];
						}
					}}
					plugins={[
						listsPlugin(),
						markdownShortcutPlugin(),
						headingsPlugin(),
						linkPlugin(),
						linkDialogPlugin({
							linkAutocompleteSuggestions: [
								process.env['NX_PUBLIC_HOME_PAGE_URL'] ?? 'https://sweatpals.com',
							],
						}),
						...(enableImagePlugin && onImageUpload ? [imagePlugin({ imageUploadHandler: onImageUpload })] : []),
						...(readOnly ? [] : [
							toolbarPlugin({
								toolbarContents: () => (
									<>
										<BoldItalicUnderlineToggles />
										<StrikeThroughSupSubToggles options={strikethroughOptions} />
										{enableListToggles && <ListsToggle />}
										{enableImagePlugin && <InsertImage />}
										{enableLinkPlugin && <CreateLink />}
										{enableUndoRedo && <UndoRedo />}
									</>
								),
								}),
							]),
					]}
				/>
			</Box>

			{helperText && !disabled && (
				<FormHelperText
					sx={{
						color: error ? Colors.error : Colors.inherit,
						mx: 0,
						mt: '5px',
						fontFamily: 'Montserrat, sans-serif',
						fontSize: '12px',
						fontWeight: '500',
					}}
				>
					{helperText}
				</FormHelperText>
			)}

			{startAdornment && readOnly && (
				<Box sx={{
					position: 'absolute',
					top: '12px',
					left: '16px',
				}}>
					{startAdornment}
				</Box>
			)}
		</Box>
	);
};
